import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * DataGrid — a tiny typed table primitive (T-0101).
 *
 * Why this exists: T-0101 ships five screens; two of them (issues, audit)
 * are tabular and a third (snapshot list) is mostly tabular. Rather than
 * inline raw `<table>` markup with duplicated header styling on every
 * screen, this primitive captures the convention so the screens stay
 * focused on their content.
 *
 * Decision: keep this *small*. It is NOT a generic data-grid library —
 * no sorting, no virtualization, no column resizing. The risk register
 * surface (T-0098) needs an Excel-grade grid and will own its own
 * Spreadjs-based primitive. This one is for read-mostly screens with
 * <500 rows.
 *
 * Tokens (per design_handoff_caliber_v2 §5):
 *   - header text:     `text-meta` (12px / 18px / 500), uppercase, ink-500
 *   - row border:      `border-ink-100`
 *   - hover bg:        `paper-50`
 *   - cell padding:    px-3 py-2 (matches prototype `.dtable` cell density)
 *
 * Accessibility: respects table semantics. `aria-rowcount` not set because
 * the grid is not virtualized (counts are intrinsic from the DOM); add
 * if/when this layers in pagination.
 */

export interface DataGridColumn<T> {
  /** Stable key — also doubles as the React `key` for the column. */
  key: string;
  /** Header label (string or any inline node). */
  header: React.ReactNode;
  /** Cell renderer. Receives the row and the row index. */
  cell: (row: T, rowIndex: number) => React.ReactNode;
  /** Optional Tailwind classes for the `<th>` cell. */
  headerClassName?: string;
  /** Optional Tailwind classes for every `<td>` cell in this column. */
  cellClassName?: string;
}

export interface DataGridProps<T> {
  columns: ReadonlyArray<DataGridColumn<T>>;
  rows: ReadonlyArray<T>;
  /** Stable React key per row — fall back to index if absent. */
  rowKey?: (row: T, rowIndex: number) => string;
  /** Optional click target on a row — flips the row to button semantics. */
  onRowClick?: (row: T, rowIndex: number) => void;
  /** Optional Tailwind classes added to the row when `selected` is true. */
  rowSelected?: (row: T, rowIndex: number) => boolean;
  /** Caption for screen readers. Visually hidden. */
  caption?: string;
  className?: string;
  emptyState?: React.ReactNode;
}

export function DataGrid<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowSelected,
  caption,
  className,
  emptyState,
}: DataGridProps<T>) {
  if (rows.length === 0 && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse text-body-sm">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-ink-100 text-left">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500',
                  col.headerClassName,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const key = rowKey ? rowKey(row, rowIndex) : String(rowIndex);
            const selected = rowSelected ? rowSelected(row, rowIndex) : false;
            const interactive = Boolean(onRowClick);
            return (
              <tr
                key={key}
                onClick={interactive ? () => onRowClick!(row, rowIndex) : undefined}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick!(row, rowIndex);
                        }
                      }
                    : undefined
                }
                aria-selected={interactive ? selected : undefined}
                className={cn(
                  'border-b border-ink-100 align-top',
                  interactive &&
                    'cursor-pointer transition-colors hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  selected && 'bg-paper-50',
                )}
              >
                {columns.map((col) => (
                  <td key={col.key} className={cn('px-3 py-2 text-ink-900', col.cellClassName)}>
                    {col.cell(row, rowIndex)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

DataGrid.displayName = 'DataGrid';
